import { Controller, useWatch } from 'react-hook-form';
import Select from 'react-select';
import { customStyles } from '../../utils/selectStyles';

const OPCOES_ESCOPO = [
    { value: 'responsavel', label: 'Somente alunos sob responsabilidade do gestor' },
    { value: 'unidade', label: 'Alunos dele e das unidades vinculadas a ele' },
    { value: 'todos', label: 'Qualquer aluno da empresa' },
];

function SeletorEscopo({ control, name, label }) {
    return (
        <div className="mt-3">
            <label className="form-label text-primary fw-medium small mb-1" htmlFor={name}>{label}</label>
            <Controller
                name={name}
                control={control}
                defaultValue="responsavel"
                render={({ field: { value, onChange } }) => (
                    <Select
                        inputId={name}
                        value={OPCOES_ESCOPO.find((o) => o.value === value) || OPCOES_ESCOPO[0]}
                        onChange={(selected) => onChange(selected ? selected.value : 'responsavel')}
                        options={OPCOES_ESCOPO}
                        styles={customStyles}
                        isClearable={false}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                )}
            />
        </div>
    );
}

function PermissoesGestorTab({ control }) {
    const [podeEditar, podeResetar] = useWatch({
        control,
        name: ['gestor_pode_cadastrar_editar_usuario', 'gestor_pode_resetar_senha'],
    });

    return (
        <div className="row">
            <div className="col-12 col-lg-8">
                <div className="card border-0 shadow-sm">
                    <div className="card-body p-4">
                        <h5 className="text-primary fw-semibold mb-2">Permissões do perfil Gestor</h5>
                        <p className="text-muted small mb-4">
                            Configure o que usuários com perfil Gestor podem fazer na plataforma.
                            As alterações valem para todos os gestores.
                        </p>

                        <div className="d-flex flex-column gap-4">
                            <div className="d-flex align-items-start justify-content-between gap-3">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="gestor_pode_cadastrar_editar_usuario">
                                        Cadastrar e editar usuários
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Permite adicionar novos alunos e editar alunos dentro do alcance abaixo.
                                        Com desligado, o gestor ainda visualiza a lista, mas sem ações de cadastro ou edição.
                                    </p>
                                    {podeEditar !== false && (
                                        <SeletorEscopo
                                            control={control}
                                            name="gestor_escopo_edicao"
                                            label="Quais alunos o gestor pode editar (vale também para atribuir cursos)"
                                        />
                                    )}
                                </div>
                                <Controller
                                    name="gestor_pode_cadastrar_editar_usuario"
                                    control={control}
                                    defaultValue={true}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="gestor_pode_cadastrar_editar_usuario"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>

                            {podeEditar !== false && (
                                <div className="d-flex align-items-start justify-content-between gap-3 border-top pt-4">
                                    <div>
                                        <label className="form-label text-primary fw-medium mb-1" htmlFor="gestor_pode_atribuir_cursos">
                                            Atribuir cursos a alunos
                                        </label>
                                        <p className="text-muted small mb-0">
                                            Permite selecionar cursos ao cadastrar ou editar um aluno.
                                        </p>
                                    </div>
                                    <Controller
                                        name="gestor_pode_atribuir_cursos"
                                        control={control}
                                        defaultValue={true}
                                        render={({ field: { value, onChange } }) => (
                                            <div className="form-check form-switch flex-shrink-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="gestor_pode_atribuir_cursos"
                                                    checked={Boolean(value)}
                                                    onChange={(e) => onChange(e.target.checked)}
                                                />
                                            </div>
                                        )}
                                    />
                                </div>
                            )}

                            <div className="d-flex align-items-start justify-content-between gap-3 border-top pt-4">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="gestor_pode_resetar_senha">
                                        Resetar senha de alunos
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Permite resetar a senha de alunos para a matrícula padrão, dentro do alcance abaixo.
                                    </p>
                                    {podeResetar !== false && (
                                        <SeletorEscopo
                                            control={control}
                                            name="gestor_escopo_reset_senha"
                                            label="De quais alunos o gestor pode resetar a senha"
                                        />
                                    )}
                                </div>
                                <Controller
                                    name="gestor_pode_resetar_senha"
                                    control={control}
                                    defaultValue={true}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="gestor_pode_resetar_senha"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>

                            <div className="d-flex align-items-start justify-content-between gap-3 border-top pt-4">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="gestor_ver_todos_cursos">
                                        Ver todos os cursos
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Ligado: o gestor vê todos os cursos da plataforma. Desligado: vê apenas os cursos
                                        vinculados a ele; nesse caso Admin/RH pode definir esses cursos no cadastro do gestor
                                        e o gestor atribui aos alunos pelo modal de edição.
                                    </p>
                                </div>
                                <Controller
                                    name="gestor_ver_todos_cursos"
                                    control={control}
                                    defaultValue={true}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="gestor_ver_todos_cursos"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm mt-4">
                    <div className="card-body p-4">
                        <h5 className="text-primary fw-semibold mb-2">Agendamentos</h5>
                        <p className="text-muted small mb-4">
                            Defina quais perfis podem criar agendamentos e quais enxergam todas as unidades.
                            O Administrador sempre pode criar e ver todas as unidades.
                        </p>

                        <div className="d-flex flex-column gap-4">
                            <div className="d-flex align-items-start justify-content-between gap-3">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="gestor_pode_criar_agendamento">
                                        Gestor pode criar agendamentos
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Habilita o botão de novo agendamento para o perfil Gestor.
                                    </p>
                                </div>
                                <Controller
                                    name="gestor_pode_criar_agendamento"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="gestor_pode_criar_agendamento"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>

                            <div className="d-flex align-items-start justify-content-between gap-3 border-top pt-4">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="rh_pode_criar_agendamento">
                                        RH pode criar agendamentos
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Habilita o botão de novo agendamento para o perfil RH.
                                    </p>
                                </div>
                                <Controller
                                    name="rh_pode_criar_agendamento"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="rh_pode_criar_agendamento"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>

                            <div className="d-flex align-items-start justify-content-between gap-3 border-top pt-4">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="gestor_agenda_todas_unidades">
                                        Gestor pode agendar para todas as unidades
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Ligado: o gestor vê todas as unidades no agendamento e na listagem.
                                        Desligado: apenas as unidades vinculadas a ele.
                                    </p>
                                </div>
                                <Controller
                                    name="gestor_agenda_todas_unidades"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="gestor_agenda_todas_unidades"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>

                            <div className="d-flex align-items-start justify-content-between gap-3 border-top pt-4">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="rh_agenda_todas_unidades">
                                        RH pode agendar para todas as unidades
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Ligado: o RH vê todas as unidades no agendamento e na listagem.
                                        Desligado: apenas as unidades vinculadas a ele.
                                    </p>
                                </div>
                                <Controller
                                    name="rh_agenda_todas_unidades"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="rh_agenda_todas_unidades"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm mt-4">
                    <div className="card-body p-4">
                        <h5 className="text-primary fw-semibold mb-2">Importação de usuários</h5>
                        <p className="text-muted small mb-4">
                            Defina quais perfis podem importar usuários em massa (planilha).
                            O Administrador sempre pode.
                        </p>

                        <div className="d-flex flex-column gap-4">
                            <div className="d-flex align-items-start justify-content-between gap-3">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="gestor_pode_importar_usuario">
                                        Gestor pode importar usuários
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Habilita o botão de importação em massa para o perfil Gestor.
                                    </p>
                                </div>
                                <Controller
                                    name="gestor_pode_importar_usuario"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="gestor_pode_importar_usuario"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>

                            <div className="d-flex align-items-start justify-content-between gap-3 border-top pt-4">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="rh_pode_importar_usuario">
                                        RH pode importar usuários
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Habilita o botão de importação em massa para o perfil RH.
                                    </p>
                                </div>
                                <Controller
                                    name="rh_pode_importar_usuario"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="rh_pode_importar_usuario"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm mt-4">
                    <div className="card-body p-4">
                        <h5 className="text-primary fw-semibold mb-2">Exportação de usuários</h5>
                        <p className="text-muted small mb-4">
                            Defina quais perfis podem baixar a lista de usuários em Excel.
                            O Administrador sempre pode.
                        </p>

                        <div className="d-flex flex-column gap-4">
                            <div className="d-flex align-items-start justify-content-between gap-3">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="gestor_pode_exportar_usuario">
                                        Gestor pode baixar a lista de usuários
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Habilita o botão Excel da lista de usuários para o perfil Gestor.
                                    </p>
                                </div>
                                <Controller
                                    name="gestor_pode_exportar_usuario"
                                    control={control}
                                    defaultValue={true}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="gestor_pode_exportar_usuario"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>

                            <div className="d-flex align-items-start justify-content-between gap-3 border-top pt-4">
                                <div>
                                    <label className="form-label text-primary fw-medium mb-1" htmlFor="rh_pode_exportar_usuario">
                                        RH pode baixar a lista de usuários
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Habilita o botão Excel da lista de usuários para o perfil RH.
                                    </p>
                                </div>
                                <Controller
                                    name="rh_pode_exportar_usuario"
                                    control={control}
                                    defaultValue={true}
                                    render={({ field: { value, onChange } }) => (
                                        <div className="form-check form-switch flex-shrink-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="rh_pode_exportar_usuario"
                                                checked={Boolean(value)}
                                                onChange={(e) => onChange(e.target.checked)}
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PermissoesGestorTab;
